import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    BreadCrumbs,
    PageTitleWithToolTip,
    CircularLoading,
    AddButton,
    DeleteModal,
} from "../../Common";
import CreateIcon from '@mui/icons-material/Create';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TableRowsIcon from '@mui/icons-material/TableRows';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import toast from 'react-hot-toast';
import Routes from '../../../routes';
import { trakrefBlue, Grey2, Grey4 } from '../../../color_utils';
import { parseRuleset, buildRuleTree, buildReferenceForest, flattenRuleTree, stripBuilderState, validateRuleset } from './rulesetUtils';
import { downloadExport, readImportFile } from './importExportUtils';
import RuleEditModal from './RuleEditModal';
import RuleTree from './RuleTree';

// POC is shipping read-only for now: adding, editing, deleting, importing and
// saving rules are all hidden. Flip this to false to restore the edit tooling.
const READ_ONLY = true;

const BusinessRuleDetails = () => {
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [businessRule, setBusinessRule] = useState(null);
    const [ruleset, setRuleset] = useState(null);
    const [parseError, setParseError] = useState(null);
    const [view, setView] = useState('table');
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [version, setVersion] = useState(0);
    const [editNode, setEditNode] = useState(null);
    const [deleteNode, setDeleteNode] = useState(null);
    const importInputRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(Routes.BusinessRules.GetById(id));
                if (response.status !== 200) {
                    toast.error('Error loading the business rule.', { duration: 6000 });
                    setLoading(false);
                    return;
                }
                const data = await response.json();
                setBusinessRule(data);
                try {
                    setRuleset(parseRuleset(data.json));
                } catch (error) {
                    setParseError(error.message);
                }
            } catch (error) {
                toast.error('Error loading the business rule.', { duration: 6000 });
            }
            setLoading(false);
        };

        fetchData();
    }, [id]);

    const tree = useMemo(
        () => (ruleset ? buildRuleTree(ruleset.groups) : []),
        // version invalidates the memo after in-place rule edits
        [ruleset, version]
    );
    const rows = useMemo(() => flattenRuleTree(tree), [tree]);
    const showWorkflowColumn = useMemo(() => tree.length > 1, [tree]);
    const referenceForest = useMemo(
        () => (ruleset ? buildReferenceForest(ruleset.groups) : []),
        // version invalidates the memo after in-place rule edits
        [ruleset, version]
    );
    const ruleNames = useMemo(
        () => [...new Set(rows.map(row => row.name))].sort(),
        [rows]
    );
    const validation = useMemo(
        () => (ruleset ? validateRuleset(ruleset.groups) : { errors: [], maxDepth: 0 }),
        // version invalidates the memo after in-place rule edits
        [ruleset, version]
    );

    const onRuleEdited = () => {
        if (editNode && editNode.isNew) {
            editNode.parentArray.push(editNode.ref);
            toast.success(
                tree.length > 1
                    ? `Rule added to workflow "${ruleset.groups[0].name}" — save the ruleset to persist.`
                    : 'Rule added — save the ruleset to persist.',
                { duration: 6000 }
            );
        }
        setDirty(true);
        setVersion(v => v + 1);
    };

    const addRule = () => {
        const ref = { RuleName: '', Expression: '', ErrorMessage: '' };
        setEditNode({
            id: 'new-rule',
            ref,
            parentArray: ruleset.groups[0].rules,
            isNew: true,
            name: '',
            operator: '',
            expression: '',
            errorMessage: '',
            enabled: undefined,
            children: [],
        });
    };

    const confirmDelete = () => {
        const index = deleteNode.parentArray ? deleteNode.parentArray.indexOf(deleteNode.ref) : -1;
        if (index >= 0) {
            deleteNode.parentArray.splice(index, 1);
            setDirty(true);
            setVersion(v => v + 1);
            toast.success(`Rule "${deleteNode.name}" removed — save the ruleset to persist.`, { duration: 6000 });
        } else {
            toast.error('Could not find the rule in the ruleset.', { duration: 6000 });
        }
        setDeleteNode(null);
    };

    const exportRuleset = () => {
        if (ruleset) stripBuilderState(ruleset.groups);
        downloadExport(businessRule, ruleset ? ruleset.root : null);
    };

    const importRuleset = async (file) => {
        try {
            const { meta, ruleset: imported } = await readImportFile(file);
            setRuleset(imported);
            setParseError(null);
            setVersion(v => v + 1);
            setDirty(true);
            if (meta && meta.name && meta.name !== businessRule.name) {
                toast(`Note: this file was exported from ruleset "${meta.name}".`, { duration: 8000, icon: '⚠️' });
            }
            toast.success('Ruleset imported — review the rules and click SAVE RULESET to persist.', { duration: 6000 });
        } catch (error) {
            toast.error(`Could not import: ${error.message}`, { duration: 8000 });
        }
    };

    const saveRuleset = async () => {
        // dangling/duplicate/cyclic rule references silently break the nightly
        // engine (tags are keyed by rule name), so they block the save outright
        if (validation.errors.length > 0) {
            toast.error(`Cannot save — fix the rule reference problems listed above the rules.`, { duration: 8000 });
            return;
        }
        setSaving(true);
        stripBuilderState(ruleset.groups);
        try {
            const response = await fetch(Routes.BusinessRules.Save(), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    id: businessRule.id,
                    json: JSON.stringify(ruleset.root, null, 2)
                })
            });

            if (response.status === 200) {
                const data = await response.json();
                setBusinessRule(data);
                setDirty(false);
                toast.success('The ruleset has been successfully saved.', { duration: 6000 });
            } else if (response.status === 400) {
                const errors = await response.json();
                const messages = Array.isArray(errors)
                    ? errors.map(e => e.exceptionMessage).filter(Boolean).join('\n')
                    : '';
                toast.error(messages || 'There was an error saving the ruleset.', { duration: 6000 });
            } else {
                toast.error('There was an error saving the ruleset. Please try again.', { duration: 6000 });
            }
        } catch (error) {
            toast.error('There was an error saving the ruleset. Please try again.', { duration: 6000 });
        }
        setSaving(false);
    };

    if (loading) {
        return <CircularLoading />;
    }

    if (!businessRule) {
        return (
            <Alert severity="error">The requested business rule could not be loaded.</Alert>
        );
    }

    const headerCellSx = { fontWeight: 600, whiteSpace: 'nowrap' };

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Geek Command Center', to: `/GeekCommandCenter` },
                    { text: 'Manage Business Rules', to: `/ManageBusinessRules` },
                    { text: businessRule.name }
                ]}
            />
            <Grid
                container
                direction="row"
                alignItems="center"
            >
                <Grid item xs={12} md={6} lg={6} mb={1}>
                    <PageTitleWithToolTip
                        pageTitle={businessRule.name}
                        toolTipText={READ_ONLY
                            ? "Read-only view of this ruleset. Switch between the tabular and hierarchical views to inspect the rules."
                            : "Parsed view of this ruleset. Edit individual rules in the popup, then save the ruleset to persist your changes."}
                    />
                    <Typography sx={{ color: Grey2, fontSize: '13px' }}>
                        {[businessRule.type, businessRule.subType].filter(Boolean).join(' / ') || 'No type'}
                        {businessRule.updateDate ? ` — last updated ${new Date(businessRule.updateDate).toLocaleDateString()}` : ''}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={6} align="right" mb={1}>
                    {!READ_ONLY && ruleset && !parseError && (
                        <span style={{ marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }}>
                            <AddButton
                                text="Add rule"
                                onClick={addRule}
                            />
                        </span>
                    )}
                    {!READ_ONLY && (
                        <input
                            ref={importInputRef}
                            type="file"
                            accept=".json,application/json"
                            style={{ display: 'none' }}
                            onChange={e => {
                                const file = e.target.files && e.target.files[0];
                                e.target.value = '';
                                if (file) importRuleset(file);
                            }}
                        />
                    )}
                    <Tooltip title="Export ruleset to a file">
                        <IconButton onClick={exportRuleset} sx={{ verticalAlign: 'middle', marginRight: READ_ONLY ? '10px' : undefined }}>
                            <FileDownloadIcon sx={{ color: Grey2 }} />
                        </IconButton>
                    </Tooltip>
                    {!READ_ONLY && (
                        <Tooltip title="Import ruleset from a file (replaces the rules shown; save to persist)">
                            <IconButton onClick={() => importInputRef.current && importInputRef.current.click()} sx={{ verticalAlign: 'middle', marginRight: '10px' }}>
                                <FileUploadIcon sx={{ color: Grey2 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={view}
                        onChange={(e, newView) => newView && setView(newView)}
                        sx={{ marginRight: '15px', verticalAlign: 'middle' }}
                    >
                        <ToggleButton value="table" aria-label="Tabular view">
                            <Tooltip title="Tabular view">
                                <TableRowsIcon fontSize="small" />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="tree" aria-label="Hierarchical view">
                            <Tooltip title="Hierarchical view">
                                <AccountTreeIcon fontSize="small" />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                    {!READ_ONLY && (
                        <Button
                            variant='contained'
                            disabled={!dirty || saving || !ruleset || validation.errors.length > 0}
                            onClick={saveRuleset}
                            style={{
                                backgroundColor: (!dirty || saving || !ruleset || validation.errors.length > 0) ? undefined : trakrefBlue,
                            }}
                        >
                            {saving ? 'SAVING...' : 'SAVE RULESET'}
                        </Button>
                    )}
                </Grid>
            </Grid>

            {!parseError && validation.errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                        {READ_ONLY
                            ? 'This ruleset has rule-reference problems:'
                            : 'This ruleset has rule-reference problems — the save button is disabled until they are fixed:'}
                    </Typography>
                    {validation.errors.map((error, i) => (
                        <Typography key={i} sx={{ fontSize: '12px', mt: 0.5 }}>• {error}</Typography>
                    ))}
                </Alert>
            )}

            {parseError ? (
                <>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This ruleset could not be parsed ({parseError}), so it is shown as raw JSON and cannot be edited here.
                    </Alert>
                    <Box
                        component="pre"
                        sx={{
                            border: `1px solid ${Grey4}`,
                            borderRadius: '6px',
                            p: 2,
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            overflow: 'auto',
                        }}
                    >
                        {businessRule.json}
                    </Box>
                </>
            ) : view === 'table' ? (
                <TableContainer sx={{ border: `1px solid ${Grey4}`, borderRadius: '6px' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {showWorkflowColumn && <TableCell sx={headerCellSx}>Workflow</TableCell>}
                                <TableCell sx={headerCellSx}>Rule</TableCell>
                                <TableCell sx={headerCellSx}>Error Message</TableCell>
                                <TableCell sx={headerCellSx}>Expression</TableCell>
                                <TableCell sx={headerCellSx} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.id} hover>
                                    {showWorkflowColumn && (
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.group}</TableCell>
                                    )}
                                    <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: `${16 + row.depth * 20}px` }}>
                                        {row.name}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 260 }}>
                                        <Typography sx={{ fontSize: '12px' }}>{row.errorMessage}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 420 }}>
                                        <Tooltip title={row.expression} placement="bottom-start">
                                            <Typography
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '12px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {row.expression}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: 90, whiteSpace: 'nowrap' }}>
                                        <Tooltip title={READ_ONLY ? 'View rule' : 'Edit rule'}>
                                            <IconButton size="small" onClick={() => setEditNode(row)}>
                                                {READ_ONLY
                                                    ? <VisibilityOutlinedIcon sx={{ color: Grey2, fontSize: 18 }} />
                                                    : <CreateIcon sx={{ color: Grey2, fontSize: 18 }} />}
                                            </IconButton>
                                        </Tooltip>
                                        {!READ_ONLY && (
                                            <Tooltip title="Delete rule">
                                                <IconButton size="small" onClick={() => setDeleteNode(row)}>
                                                    <DeleteOutlineIcon sx={{ color: Grey2, fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <>
                    <Typography sx={{ color: Grey2, fontSize: '12px', mb: 1 }}>
                        Rules nest beneath the rules whose names they reference in their expressions.
                        {validation.maxDepth > 0 &&
                            ` Longest reference chain: ${validation.maxDepth + 1} rules (the engine converges any depth in a single run).`}
                    </Typography>
                    <RuleTree
                        tree={referenceForest}
                        onEdit={node => setEditNode(node)}
                        onDelete={READ_ONLY ? undefined : node => setDeleteNode(node)}
                    />
                </>
            )}

            <RuleEditModal
                node={editNode}
                modalOpen={!!editNode}
                onClose={() => setEditNode(null)}
                onApplied={onRuleEdited}
                ruleNames={ruleNames}
                readOnly={READ_ONLY}
            />

            <DeleteModal
                open={!!deleteNode}
                title={`Delete rule "${deleteNode ? deleteNode.name : ''}"?`}
                description={
                    deleteNode && deleteNode.ref && (deleteNode.ref.Rules || []).length > 0
                        ? 'The rule and its nested sub-rules are removed from this ruleset when you save.'
                        : 'The rule is removed from this ruleset when you save.'
                }
                buttonText="Delete rule"
                showImage={false}
                onConfirm={confirmDelete}
                onClose={() => setDeleteNode(null)}
            />
        </>
    );
}

export default BusinessRuleDetails;
